/**
 * Centralized URL generation utilities.
 * This is the SOURCE OF TRUTH for URL structure across the application.
 *
 * This file is exported from studio to frontend to ensure consistency.
 * Used by:
 * - Sanity Studio resolver (locations and presentation tool)
 * - Frontend routing (pages/blog)
 * - Sitemap generation
 */

export type DocumentType = 'page' | 'post' | 'category' | 'tag' | 'author' | 'blog' | 'home'

export interface UrlParams {
  type: DocumentType
  /**
   * Path for the document (can include hierarchy for pages)
   * - For pages: 'about' or 'parent/child'
   * - For posts, categories, tags and authors: the document slug, e.g. 'my-post'
   * - For blog index: undefined
   * - For home: undefined
   */
  path?: string
  /** @deprecated Use `path` instead. Kept for backward compatibility. */
  slug?: string
}

/**
 * Generates the path (without domain) for a document.
 *
 * @example
 * buildDocumentPath({ type: 'page', path: 'about' })
 * // Returns: '/about'
 *
 * @example
 * buildDocumentPath({ type: 'page', path: 'company/team' })
 * // Returns: '/company/team'
 *
 * @example
 * buildDocumentPath({ type: 'post', path: 'my-post' })
 * // Returns: '/blog/my-post'
 *
 * @example
 * buildDocumentPath({ type: 'category', path: 'publishers' })
 * // Returns: '/blog/category/publishers'
 */
export function buildDocumentPath({ type, path, slug }: UrlParams): string {
  // Support backward compatibility: use path if provided, otherwise fall back to slug
  const documentPath = path || slug

  switch (type) {
    case 'home':
      return '/'
    case 'blog':
      return '/blog'
    case 'post':
      return documentPath ? `/blog/${documentPath}` : '/blog'
    case 'category':
      return documentPath ? `/blog/category/${documentPath}` : '/blog'
    case 'tag':
      return documentPath ? `/blog/tag/${documentPath}` : '/blog'
    case 'author':
      return documentPath ? `/blog/author/${documentPath}` : '/blog'
    case 'page':
      return documentPath ? `/${documentPath}` : '/'
    default:
      console.warn('Invalid document type:', type)
      return '/'
  }
}

/**
 * Generates the full URL (with domain) for a document.
 *
 * @example
 * buildDocumentUrl('https://example.com', { type: 'page', path: 'about' })
 * // Returns: 'https://example.com/about'
 *
 * @example
 * buildDocumentUrl('https://example.com', { type: 'page', path: 'company/team' })
 * // Returns: 'https://example.com/company/team'
 */
export function buildDocumentUrl(baseUrl: string, params: UrlParams): string {
  const docPath = buildDocumentPath(params)
  return `${baseUrl}${docPath}`
}

/**
 * Extracts document information from a URL path.
 * Useful for reverse-resolving paths to document types.
 *
 * @example
 * parseDocumentPath('/blog/my-post')
 * // Returns: { type: 'post', path: 'my-post' }
 *
 * @example
 * parseDocumentPath('/blog/category/publishers')
 * // Returns: { type: 'category', path: 'publishers' }
 *
 * @example
 * parseDocumentPath('/company/team')
 * // Returns: { type: 'page', path: 'company/team' }
 */
export function parseDocumentPath(urlPath: string): Partial<UrlParams> | null {
  // Remove trailing slash
  const normalizedPath = urlPath.replace(/\/$/, '') || '/'

  // Home page
  if (normalizedPath === '/') {
    return { type: 'home' }
  }

  if (normalizedPath === '/blog') {
    return { type: 'blog' }
  }

  const categoryMatch = normalizedPath.match(/^\/blog\/category\/([^/]+)$/)
  if (categoryMatch) {
    return { type: 'category', path: categoryMatch[1] }
  }

  const authorMatch = normalizedPath.match(/^\/blog\/author\/([^/]+)$/)
  if (authorMatch) {
    return { type: 'author', path: authorMatch[1] }
  }

  const tagMatch = normalizedPath.match(/^\/blog\/tag\/([^/]+)$/)
  if (tagMatch) {
    return { type: 'tag', path: tagMatch[1] }
  }

  const postMatch = normalizedPath.match(/^\/blog\/([^/]+)$/)
  if (postMatch) {
    return { type: 'post', path: postMatch[1] }
  }

  // Pages (including hierarchical: /about or /parent/child)
  const pageMatch = normalizedPath.match(/^\/(.+)$/)
  if (pageMatch) {
    return { type: 'page', path: pageMatch[1] }
  }

  return null
}
